import React, { useState, useRef, useCallback, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import axiosInstance from '../../../services/api/axios';
import { useUser } from '../../../context/UserContext';
import SearchInput from '../../../components/SearchInput';
import usePagination from '../../../hooks/usePagination';
import PaginationControls from '../../../components/PaginationControls';

function CustomerNotificationHistory({ property }) {
    const { userInfo } = useUser();
    const gridRef = useRef(null);
    
    const [columnDefs] = useState([
        { headerName: 'Sender', field: 'sender', filter: true },
        { headerName: 'Recipient', field: 'recipient', filter: true },
        { headerName: 'Text', field: 'text', filter: true },
        {
            headerName: 'Attachment',
            field: 'attachmentDownloadUrl',
            filter: false,
            // Backend-presigned URL (attachmentUrl is a raw S3 key, not openable)
            cellRenderer: (params) =>
                params.value ? (
                    <a
                        href={params.value}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-flamingo-600 hover:text-flamingo-700 font-medium"
                    >
                        Open
                    </a>
                ) : (
                    '—'
                ),
        },
        { headerName: 'Sent Date', field: 'sentDate', filter: true },
        { headerName: 'WhatsApp Message ID', field: 'whatsappMessageId', filter: true },
        { headerName: 'Status', field: 'status', filter: true },
    ]);

    // Create fetch function for usePagination hook
    const fetchNotificationsBackend = useCallback(async ({ page, page_size, search }) => {
        const params = {
            page: page,
            page_size: page_size,
        };

        if (property && property.propertyId) {
            params.propertyId = property.propertyId;
            params.customerId = userInfo.loginIdentifierDetails.customer.customerId;
        }

        // Add search parameter if provided
        if (search) {
            params.search = search;
        }

        const response = await axiosInstance.get('/notifications', {
            params: params,
        });

        // Handle paginated response format: { items: [...], total: 1, page: 1, ... }
        if (response?.data?.items !== undefined && response?.data?.total !== undefined) {
            return response.data;
        } else if (response?.items !== undefined && response?.total !== undefined) {
            return response;
        } else if (Array.isArray(response?.data)) {
            // If it's an array, wrap it in paginated format for backward compatibility
            return {
                items: response.data,
                total: response.data.length,
                page: page,
                page_size: page_size,
                total_pages: Math.ceil(response.data.length / page_size),
                has_next: page * page_size < response.data.length,
                has_previous: page > 1
            };
        } else if (Array.isArray(response)) {
            // If it's an array directly, wrap it in paginated format
            return {
                items: response,
                total: response.length,
                page: page,
                page_size: page_size,
                total_pages: Math.ceil(response.length / page_size),
                has_next: page * page_size < response.length,
                has_previous: page > 1
            };
        }
        return response?.data || response;
    }, [property, userInfo]);

    // Use pagination hook with manual search mode
    const {
        data: paginationData,
        pagination,
        searchInput,
        loading,
        goToPage,
        changePageSize,
        handleSearchInputChange,
        triggerSearch,
        refetch
    } = usePagination(fetchNotificationsBackend, 30, '', true, true);

    // Extract notifications from paginated response
    const rowData = useMemo(() => {
        if (!paginationData) return [];
        // Handle paginated response format: { items: [...], total: 1, page: 1, ... }
        if (paginationData?.items && Array.isArray(paginationData.items)) {
            return paginationData.items;
        } else if (Array.isArray(paginationData)) {
            return paginationData;
        }
        return [];
    }, [paginationData]);

    const defaultColDef = useMemo(() => ({
        sortable: true,
        filter: true,
        resizable: true,
    }), []);

    const onGridReady = useCallback((params) => {
        gridRef.current = params;
    }, []);

    const handleSearchTextChange = (value) => {
        handleSearchInputChange(value);
    };

    const handleSearch = useCallback(async () => {
        await triggerSearch();
    }, [triggerSearch]);

    return (
        <div className="mx-4 ">
            <h2 className="text-lg font-semibold py-4">Notification History</h2>
            <div className="mb-4">
                <SearchInput
                    value={searchInput}
                    onChange={handleSearchTextChange}
                    onSearch={handleSearch}
                    placeholder="Search notifications..."
                    disabled={loading}
                    className="w-64"
                />
            </div>
            <div className="ag-theme-quartz font-normal" style={{ height: 400, width: '100%' }}>
                <AgGridReact
                    ref={gridRef}
                    columnDefs={columnDefs}
                    rowData={rowData}
                    defaultColDef={defaultColDef}
                    onGridReady={onGridReady}
                    pagination={false}
                    rowSelection="single"
                />
                {pagination && pagination.total > 0 && (
                    <div className="mt-4">
                        <PaginationControls
                            pagination={pagination}
                            onPageChange={goToPage}
                            onPageSizeChange={changePageSize}
                            itemName="notifications"
                            variant="list"
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

export default CustomerNotificationHistory;